import { useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Separator } from '@blade/ui-kit'
import { CountMax3DigitsFormatted } from '@root/shared/core/format'

import { DevTokensStatsChart } from './dev-tokens-stats-chart/dev-tokens-stats-chart'

import type { CreatorPairsStatsModel } from '../../creator-pairs/atoms/reatom-creator-pairs-stats'

type Props = {
  model: CreatorPairsStatsModel
}

export const DevTokensStatsMigrations: React.FC<Props> = memo(({ model }) => {
  const { t } = useTranslation()

  const [state] = useAtom(model)

  if (!AsyncStateN.isSuccess(state)) {
    return undefined
  }

  const { migratedPools, totalPools } = state.value
  const notMigratedPools = totalPools - migratedPools
  const percentage = (() => {
    if (totalPools === 0 || migratedPools === 0) {
      return 0
    }
    return BigNumber(migratedPools)
      .dividedBy(totalPools)
      .multipliedBy(100)
      .decimalPlaces(2)
      .toNumber()
  })()

  return (
    <Root>
      <DevTokensStatsChart percentage={percentage} />
      <SeparatorStyled />
      <Title>
        {t('dev-tokens.total-pairs')}:{' '}
        <CountMax3DigitsFormatted count={totalPools} />
      </Title>
      <MigrationContainer>
        <MigratedLabel>
          <CountMax3DigitsFormatted count={migratedPools} />
          <span> - </span>
          <span>{t('dev-tokens.migrated')}</span>
        </MigratedLabel>
        <Separator vertical />
        <NotMigratedLabel>
          <CountMax3DigitsFormatted count={notMigratedPools} />
          <span> - </span>
          <span>{t('dev-tokens.not-migrated')}</span>
        </NotMigratedLabel>
      </MigrationContainer>
    </Root>
  )
})

const Root = styled.div`
  padding-block: 8px 8px;
`
const SeparatorStyled = styled(Separator)`
  margin-block: 8px 8px;
`

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  color: var(--text-11);
  margin-bottom: 4px;
`

const MigratedLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  color: var(--up-9);
`

const NotMigratedLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  color: var(--down-9);
`

const MigrationContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  justify-content: center;
  gap: 6px;
`
